import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Link } from "react-router-dom";
import Layout from "../components/Layout";
import { api } from "../lib/api";
import { fmtDate, fmtMoney } from "../lib/format";
import { AlertTriangle, CheckCircle2, FileText, Globe, ShieldCheck, Scale, Plus, FolderLock, Upload, Download, Trash2, Pencil, Save, X, GraduationCap, Sparkles, Loader2, Check, ExternalLink } from "lucide-react";

const TABS = [
  { id: "cases", label: "Legal cases" },
  { id: "tms", label: "FIFA TMS" },
  { id: "bodies", label: "Governing bodies" },
  { id: "licence", label: "My licence" },
];

const STATUS_TONE = {
  draft:             "pm-pill-slate",
  filed:             "pm-pill-blue",
  under_review:      "pm-pill-blue",
  hearing_scheduled: "pm-pill-red",
  awaiting_ruling:   "pm-pill-amber",
  resolved:          "pm-pill-green",
  appealed:          "pm-pill-red",
};

export default function Legal() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = TABS.some((t) => t.id === searchParams.get("tab"))
    ? searchParams.get("tab")
    : "cases";
  const [tab, setTab] = useState(initialTab);
  // Keep tab in sync with the URL — lets Dashboard licence-card deep-link
  // to /legal?tab=licence and back/forward navigation feel right.
  useEffect(() => {
    if (TABS.some((t) => t.id === searchParams.get("tab")) && searchParams.get("tab") !== tab) {
      setTab(searchParams.get("tab"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);
  const setTabAndUrl = (id) => {
    setTab(id);
    const next = new URLSearchParams(searchParams);
    next.set("tab", id);
    setSearchParams(next, { replace: true });
  };
  return (
    <Layout title="Legal hub" subtitle="Module 7 · cases, TMS, governing bodies, compliance">
      <div className="border-b border-slate-200 flex items-center gap-1 overflow-x-auto pm-scroll mb-6">
        {TABS.map((t) => (
          <button key={t.id} onClick={() => setTabAndUrl(t.id)} data-testid={`legal-tab-${t.id}`}
            className={`px-4 py-3 text-sm font-bold tracking-tight border-b-2 transition-all whitespace-nowrap ${
              tab === t.id ? "border-emerald-500 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-800"
            }`}>{t.label}</button>
        ))}
      </div>
      {tab === "cases" && <CasesTab />}
      {tab === "tms" && <TmsTab />}
      {tab === "bodies" && <BodiesTab />}
      {tab === "licence" && <LicenceTab />}
    </Layout>
  );
}

function CasesTab() {
  const [cases, setCases] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [openCaseId, setOpenCaseId] = useState(null);
  const load = () => api.get("/legal/cases").then((r) => setCases(r.data || []));
  useEffect(() => { load(); }, []);

  const remove = async (caseId) => {
    if (!window.confirm("Delete this legal case and all its uploaded documents?")) return;
    try {
      await api.delete(`/legal/cases/${caseId}`);
      load();
    } catch {
      window.alert("Could not delete this case.");
    }
  };

  return (
    <div className="space-y-3" data-testid="legal-cases-list">
      <div className="flex items-center justify-between">
        <h3 className="font-display text-xl font-bold tracking-tight">Active legal cases ({cases.length})</h3>
        <button
          className="pm-btn-primary h-10 px-4 rounded-xl text-sm font-bold flex items-center gap-2"
          data-testid="legal-new-case-btn"
          onClick={() => setShowModal(true)}
        >
          <Plus size={14} /> New legal case
        </button>
      </div>
      {cases.map((c) => {
        const initials = (c.client_name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();
        return (
        <div
          key={c.id}
          className="pm-card pm-card-hover p-5 cursor-pointer"
          data-testid={`legal-case-${c.id}`}
          onClick={() => setOpenCaseId(c.id)}
          role="button"
        >
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1.5">
                <span className="font-mono-tab text-xs text-slate-500">{c.case_number}</span>
                <span className={`pm-pill ${STATUS_TONE[c.status] || "pm-pill-slate"}`}>{(c.status || "draft").replace("_", " ")}</span>
                <span className="pm-pill pm-pill-slate">{(c.case_type || "regulatory").replace("_", " ")}</span>
              </div>
              <div className="font-display font-bold text-lg">{c.summary || "—"}</div>
              <div className="text-xs text-slate-500 mt-1">
                {c.client_name || "Unlinked"} · {c.jurisdiction || "—"} · vs {c.opposing_party || "—"} · counsel: {c.counsel || "—"} · governing body: {c.governing_body || "—"}
              </div>
              {c.next_deadline && (
                <div className="text-xs text-amber-700 font-mono-tab mt-2 inline-flex items-center gap-1">
                  <AlertTriangle size={11} /> Next deadline: {fmtDate(c.next_deadline)}
                </div>
              )}
              <div className="mt-3 flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                <button
                  className="pm-btn-secondary h-8 px-3 rounded-lg text-[11px] font-bold flex items-center gap-1.5"
                  data-testid={`legal-vault-${c.id}`}
                  title="Open case detail & document vault"
                  onClick={() => setOpenCaseId(c.id)}
                >
                  <FolderLock size={11} /> Open case
                </button>
                <span className="text-[10px] text-slate-400 font-mono-tab">{c.documents_count ?? 0} docs</span>
                <button
                  onClick={() => remove(c.id)}
                  className="ml-auto h-8 w-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-rose-600 hover:bg-rose-50"
                  title="Delete case"
                  data-testid={`legal-case-delete-${c.id}`}
                >
                  <Trash2 size={12} />
                </button>
              </div>
            </div>
            <div className="w-12 h-12 rounded-lg bg-slate-100 flex items-center justify-center font-display font-black text-slate-600 shrink-0" title={c.client_name}>
              {initials}
            </div>
          </div>
        </div>
        );
      })}
      {cases.length === 0 && (
        <div className="pm-card p-8 text-center" data-testid="legal-cases-empty">
          <Scale size={28} className="mx-auto text-slate-300 mb-2" />
          <div className="font-bold text-slate-700">No legal cases on file.</div>
          <div className="text-xs text-slate-500 mt-1">
            Click <b>New legal case</b> to file one — drop a PDF and we'll auto-detect the player + case type with Claude vision.
          </div>
        </div>
      )}
      {showModal && (
        <NewLegalCaseModal
          onClose={() => setShowModal(false)}
          onCreated={() => { setShowModal(false); load(); }}
        />
      )}
      {openCaseId && (
        <LegalCaseDrawer
          caseId={openCaseId}
          onClose={() => setOpenCaseId(null)}
          onSaved={load}
          onDeleted={() => { setOpenCaseId(null); load(); }}
        />
      )}
    </div>
  );
}

const CASE_TYPES = [
  "contract_dispute", "transfer_dispute", "work_permit_appeal", "doping",
  "disciplinary", "image_rights", "solidarity", "training_compensation",
  "tax_wage", "regulatory",
];

function NewLegalCaseModal({ onClose, onCreated }) {
  const [file, setFile] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [scanned, setScanned] = useState(null); // {suggestions, ai_meta, used_vision}
  const [form, setForm] = useState({
    client_id: "", case_type: "contract_dispute", jurisdiction: "",
    opposing_party: "", counsel: "", governing_body: "", summary: "", next_deadline: "",
  });
  const [allClients, setAllClients] = useState([]);
  const [clientSearch, setClientSearch] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/clients").then((r) => {
      const arr = Array.isArray(r.data) ? r.data : r.data?.clients || [];
      setAllClients(arr.map((c) => ({ id: c.id, full_name: c.full_name, current_club: c.current_club })));
    }).catch(() => {});
  }, []);

  const pickedClient = allClients.find((c) => c.id === form.client_id);
  const filtered = !clientSearch ? [] : allClients.filter((c) =>
    (c.full_name || "").toLowerCase().includes(clientSearch.toLowerCase())
  ).slice(0, 6);

  const scan = async (f) => {
    setFile(f);
    setScanned(null);
    if (!f) return;
    setScanning(true);
    setErr("");
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post("/legal/cases/scan-document", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setScanned(r.data);
      const meta = r.data?.ai_meta || {};
      const top = (r.data?.suggestions || [])[0];
      setForm((prev) => ({
        ...prev,
        client_id: top && top.confidence >= 0.7 ? top.id : prev.client_id,
        case_type: meta.case_type && CASE_TYPES.includes(meta.case_type) ? meta.case_type : prev.case_type,
        opposing_party: prev.opposing_party || meta.opposing_party || "",
        governing_body: prev.governing_body || meta.governing_body || "",
        summary: prev.summary || meta.summary || "",
      }));
    } catch (e) {
      setErr(e?.response?.data?.detail || "Scan failed. You can still fill the form manually.");
    } finally {
      setScanning(false);
    }
  };

  const submit = async () => {
    if (!form.jurisdiction.trim()) {
      setErr("Jurisdiction is required (e.g. Netherlands, Spain, FIFA).");
      return;
    }
    setBusy(true);
    setErr("");
    try {
      const created = await api.post("/legal/cases", {
        client_id: form.client_id || null,
        case_type: form.case_type,
        jurisdiction: form.jurisdiction.trim(),
        opposing_party: form.opposing_party.trim() || null,
        counsel: form.counsel.trim() || null,
        governing_body: form.governing_body.trim() || null,
        summary: form.summary.trim() || null,
        next_deadline: form.next_deadline || null,
        status: "draft",
      });
      if (file && created.data?.id) {
        const fd = new FormData();
        fd.append("file", file);
        await api.post(`/legal/cases/${created.data.id}/document`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      onCreated();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not create case.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4" data-testid="new-legal-case-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[92vh] overflow-y-auto">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between sticky top-0 bg-white z-10">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-violet-600 flex items-center gap-1.5">
              <Sparkles size={11} /> File a new legal case
            </div>
            <div className="font-display text-lg font-black tracking-tight">New legal case</div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="new-legal-case-close">
            <X size={16} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* File upload + AI scan */}
          <div>
            <label className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 block mb-2">
              Document (optional — auto-detects client & case type)
            </label>
            {!file ? (
              <label
                className="flex flex-col items-center justify-center border-2 border-dashed border-slate-300 rounded-xl p-6 text-center cursor-pointer hover:border-emerald-400 hover:bg-emerald-50/40 transition-colors"
                data-testid="legal-doc-dropzone"
              >
                <Upload size={20} className="text-slate-400 mb-2" />
                <div className="text-sm font-bold text-slate-700">Drop or click to upload a PDF or image</div>
                <div className="text-[11px] text-slate-500 mt-0.5">Max 25 MB · we'll scan with Claude vision</div>
                <input
                  type="file"
                  className="hidden"
                  accept="application/pdf,image/*"
                  onChange={(e) => scan(e.target.files?.[0] || null)}
                  data-testid="legal-doc-input"
                />
              </label>
            ) : (
              <div className="border border-slate-200 rounded-xl p-3 flex items-center gap-3" data-testid="legal-doc-selected">
                <FileText size={18} className="text-emerald-600" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-bold text-slate-900 truncate">{file.name}</div>
                  <div className="text-[11px] text-slate-500 font-mono-tab">{(file.size / 1024).toFixed(0)} KB</div>
                </div>
                {scanning && <Loader2 size={14} className="animate-spin text-violet-600" />}
                <button
                  onClick={() => { setFile(null); setScanned(null); }}
                  className="text-slate-400 hover:text-rose-600"
                  data-testid="legal-doc-clear"
                >
                  <X size={14} />
                </button>
              </div>
            )}
          </div>

          {/* AI suggestions */}
          {scanned && (scanned.suggestions || []).length > 0 && (
            <div className="border border-violet-200 bg-violet-50/40 rounded-xl p-3" data-testid="legal-ai-suggestions">
              <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-violet-700 flex items-center gap-1.5 mb-2">
                <Sparkles size={11} />
                AI suggestions {scanned.used_vision && <span>· Claude vision</span>}
              </div>
              <div className="space-y-1.5">
                {scanned.suggestions.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, client_id: s.id }))}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors ${
                      form.client_id === s.id ? "bg-violet-100 ring-2 ring-violet-300" : "bg-white hover:bg-violet-100"
                    }`}
                    data-testid={`legal-ai-pick-${s.id}`}
                  >
                    <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center font-bold text-slate-700 text-xs">
                      {s.full_name?.split(" ").map((w) => w[0]).slice(0, 2).join("")}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-bold truncate">{s.full_name}</div>
                      <div className="text-[11px] text-slate-500 truncate italic">{s.reason}</div>
                    </div>
                    <div className={`text-[10px] font-bold uppercase tracking-wider ${
                      s.confidence >= 0.85 ? "text-emerald-700" : s.confidence >= 0.6 ? "text-amber-700" : "text-slate-500"
                    }`}>
                      {Math.round(s.confidence * 100)}%
                    </div>
                    {form.client_id === s.id && <Check size={14} className="text-violet-600" />}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Client picker */}
          <div>
            <label className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 block mb-2">
              Client {pickedClient && <span className="text-emerald-700 normal-case tracking-normal">· {pickedClient.full_name}</span>}
            </label>
            <input
              type="text"
              value={clientSearch}
              onChange={(e) => setClientSearch(e.target.value)}
              placeholder={pickedClient ? "Change client (search by name)…" : "Search client by name (or leave empty)…"}
              className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm"
              data-testid="legal-client-search"
            />
            {filtered.length > 0 && (
              <div className="mt-2 space-y-1 max-h-32 overflow-y-auto border border-slate-200 rounded-lg p-1">
                {filtered.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => { setForm((f) => ({ ...f, client_id: c.id })); setClientSearch(""); }}
                    className={`w-full flex items-center justify-between px-3 py-2 rounded text-sm text-left hover:bg-slate-50`}
                    data-testid={`legal-client-manual-${c.id}`}
                  >
                    <span className="truncate">{c.full_name}</span>
                    <span className="text-[11px] text-slate-500 truncate ml-2">{c.current_club}</span>
                  </button>
                ))}
              </div>
            )}
            {form.client_id && (
              <button
                type="button"
                onClick={() => setForm((f) => ({ ...f, client_id: "" }))}
                className="text-[11px] text-slate-500 hover:text-rose-600 mt-1.5"
                data-testid="legal-client-clear"
              >
                Clear client link
              </button>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <LabeledField label="Case type">
              <select
                value={form.case_type}
                onChange={(e) => setForm((f) => ({ ...f, case_type: e.target.value }))}
                className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm bg-white"
                data-testid="legal-case-type"
              >
                {CASE_TYPES.map((t) => (
                  <option key={t} value={t}>{t.replace(/_/g, " ")}</option>
                ))}
              </select>
            </LabeledField>
            <LabeledField label="Jurisdiction *">
              <input
                type="text"
                value={form.jurisdiction}
                onChange={(e) => setForm((f) => ({ ...f, jurisdiction: e.target.value }))}
                placeholder="Netherlands · Spain · FIFA · CAS"
                className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm"
                data-testid="legal-jurisdiction"
              />
            </LabeledField>
            <LabeledField label="Opposing party">
              <input
                type="text"
                value={form.opposing_party}
                onChange={(e) => setForm((f) => ({ ...f, opposing_party: e.target.value }))}
                className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm"
                data-testid="legal-opposing-party"
              />
            </LabeledField>
            <LabeledField label="Counsel">
              <input
                type="text"
                value={form.counsel}
                onChange={(e) => setForm((f) => ({ ...f, counsel: e.target.value }))}
                className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm"
                data-testid="legal-counsel"
              />
            </LabeledField>
            <LabeledField label="Governing body">
              <input
                type="text"
                value={form.governing_body}
                onChange={(e) => setForm((f) => ({ ...f, governing_body: e.target.value }))}
                placeholder="FIFA · UEFA · KNVB · CAS"
                className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm"
                data-testid="legal-governing-body"
              />
            </LabeledField>
            <LabeledField label="Next deadline">
              <input
                type="date"
                value={form.next_deadline}
                onChange={(e) => setForm((f) => ({ ...f, next_deadline: e.target.value }))}
                className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm"
                data-testid="legal-next-deadline"
              />
            </LabeledField>
          </div>

          <LabeledField label="Summary">
            <textarea
              value={form.summary}
              onChange={(e) => setForm((f) => ({ ...f, summary: e.target.value }))}
              rows={3}
              placeholder="One-line summary of the dispute or filing"
              className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm resize-none"
              data-testid="legal-summary"
            />
          </LabeledField>

          {err && (
            <div className="text-xs font-bold text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2" data-testid="legal-form-error">
              {err}
            </div>
          )}
        </div>

        <div className="px-5 py-4 flex items-center gap-3 border-t border-slate-100 sticky bottom-0 bg-white">
          <button onClick={onClose} className="pm-btn-secondary h-10 px-4 rounded-xl text-sm font-bold" data-testid="legal-modal-cancel">
            Cancel
          </button>
          <div className="flex-1" />
          <button
            onClick={submit}
            disabled={busy || scanning}
            className="pm-btn-primary h-10 px-5 rounded-xl text-sm font-bold flex items-center gap-1.5 disabled:opacity-40"
            data-testid="legal-modal-submit"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            {file ? "Create case + upload" : "Create case"}
          </button>
        </div>
      </div>
    </div>
  );
}

function LabeledField({ label, children }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 block mb-1.5">{label}</span>
      {children}
    </label>
  );
}

const STATUS_OPTIONS = [
  "draft", "filed", "under_review", "hearing_scheduled", "awaiting_ruling", "resolved", "appealed",
];

function LegalCaseDrawer({ caseId, onClose, onSaved, onDeleted }) {
  const [data, setData] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const load = async () => {
    try {
      const r = await api.get(`/legal/cases/${caseId}`);
      setData(r.data);
      setForm({
        case_type: r.data.case_type || "regulatory",
        status: r.data.status || "draft",
        jurisdiction: r.data.jurisdiction || "",
        opposing_party: r.data.opposing_party || "",
        counsel: r.data.counsel || "",
        governing_body: r.data.governing_body || "",
        next_deadline: (r.data.next_deadline || "").slice(0, 10),
        summary: r.data.summary || "",
      });
    } catch (e) {
      setErr("Could not load case.");
    }
    try {
      const r2 = await api.get(`/legal/cases/${caseId}/documents`);
      setDocuments(r2.data?.documents || []);
    } catch {
      setDocuments([]);
    }
  };

  useEffect(() => { if (caseId) load(); }, [caseId]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    if (!form) return;
    setBusy(true);
    setErr("");
    try {
      const payload = {
        case_type: form.case_type,
        status: form.status,
        jurisdiction: form.jurisdiction.trim(),
        opposing_party: form.opposing_party.trim() || null,
        counsel: form.counsel.trim() || null,
        governing_body: form.governing_body.trim() || null,
        summary: form.summary.trim() || null,
        next_deadline: form.next_deadline || null,
      };
      await api.patch(`/legal/cases/${caseId}`, payload);
      await load();
      onSaved?.();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not save changes.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Delete this legal case and all its uploaded documents?")) return;
    try {
      await api.delete(`/legal/cases/${caseId}`);
      onDeleted?.();
    } catch {
      setErr("Could not delete this case.");
    }
  };

  const uploadDoc = async (file) => {
    if (!file) return;
    setUploading(true);
    setErr("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/legal/cases/${caseId}/document`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await load();
      onSaved?.();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not upload document.");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const viewDoc = async (d) => {
    try {
      const token = localStorage.getItem("pm_token");
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/legal/cases/${caseId}/documents/${d.id}`;
      const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error("download failed");
      const blob = await r.blob();
      window.open(URL.createObjectURL(blob), "_blank", "noopener");
    } catch {
      setErr("Could not open document.");
    }
  };

  const humanSize = (n) => {
    if (!n) return "—";
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
    return `${(n / 1024 / 1024).toFixed(1)} MB`;
  };

  if (!data || !form) {
    return (
      <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4" data-testid="legal-case-drawer">
        <div className="bg-white rounded-2xl shadow-2xl p-10 text-sm text-slate-500 flex items-center gap-3">
          <Loader2 size={16} className="animate-spin" /> Loading case…
        </div>
      </div>
    );
  }

  const isDirty = (
    form.case_type !== (data.case_type || "regulatory") ||
    form.status !== (data.status || "draft") ||
    form.jurisdiction !== (data.jurisdiction || "") ||
    form.opposing_party !== (data.opposing_party || "") ||
    form.counsel !== (data.counsel || "") ||
    form.governing_body !== (data.governing_body || "") ||
    form.next_deadline !== ((data.next_deadline || "").slice(0, 10)) ||
    form.summary !== (data.summary || "")
  );

  return (
    <div className="fixed inset-0 z-[60] flex items-stretch justify-end bg-black/50" data-testid="legal-case-drawer" onClick={onClose}>
      <div
        className="bg-white shadow-2xl w-full max-w-2xl h-full overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-100 sticky top-0 bg-white z-10">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="font-mono-tab text-xs text-slate-500">{data.case_number}</span>
                <span className={`pm-pill ${STATUS_TONE[form.status] || "pm-pill-slate"}`}>
                  {(form.status || "draft").replace(/_/g, " ")}
                </span>
                <span className="pm-pill pm-pill-slate">{(form.case_type || "regulatory").replace(/_/g, " ")}</span>
              </div>
              <div className="font-display text-xl font-black tracking-tight truncate">
                {data.summary || "Legal case"}
              </div>
              {data.client_name && (
                <Link
                  to={`/clients/${data.client_id}`}
                  className="text-xs text-emerald-700 hover:text-emerald-900 inline-flex items-center gap-1 mt-1"
                  data-testid="legal-drawer-client-link"
                >
                  {data.client_name}{data.client_current_club ? ` · ${data.client_current_club}` : ""} →
                </Link>
              )}
            </div>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="legal-drawer-close">
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Form */}
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <LabeledField label="Status">
              <select
                value={form.status}
                onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
                className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm bg-white"
                data-testid="legal-drawer-status"
              >
                {STATUS_OPTIONS.map((s) => (
                  <option key={s} value={s}>{s.replace(/_/g, " ")}</option>
                ))}
              </select>
            </LabeledField>
            <LabeledField label="Case type">
              <select
                value={form.case_type}
                onChange={(e) => setForm((f) => ({ ...f, case_type: e.target.value }))}
                className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm bg-white"
                data-testid="legal-drawer-case-type"
              >
                {CASE_TYPES.map((t) => (
                  <option key={t} value={t}>{t.replace(/_/g, " ")}</option>
                ))}
              </select>
            </LabeledField>
            <LabeledField label="Jurisdiction">
              <input
                type="text"
                value={form.jurisdiction}
                onChange={(e) => setForm((f) => ({ ...f, jurisdiction: e.target.value }))}
                className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm"
                data-testid="legal-drawer-jurisdiction"
              />
            </LabeledField>
            <LabeledField label="Next deadline">
              <input
                type="date"
                value={form.next_deadline}
                onChange={(e) => setForm((f) => ({ ...f, next_deadline: e.target.value }))}
                className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm"
                data-testid="legal-drawer-next-deadline"
              />
            </LabeledField>
            <LabeledField label="Opposing party">
              <input
                type="text"
                value={form.opposing_party}
                onChange={(e) => setForm((f) => ({ ...f, opposing_party: e.target.value }))}
                className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm"
                data-testid="legal-drawer-opposing-party"
              />
            </LabeledField>
            <LabeledField label="Counsel">
              <input
                type="text"
                value={form.counsel}
                onChange={(e) => setForm((f) => ({ ...f, counsel: e.target.value }))}
                className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm"
                data-testid="legal-drawer-counsel"
              />
            </LabeledField>
            <LabeledField label="Governing body">
              <input
                type="text"
                value={form.governing_body}
                onChange={(e) => setForm((f) => ({ ...f, governing_body: e.target.value }))}
                className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm"
                data-testid="legal-drawer-governing-body"
              />
            </LabeledField>
          </div>

          <LabeledField label="Summary">
            <textarea
              value={form.summary}
              onChange={(e) => setForm((f) => ({ ...f, summary: e.target.value }))}
              rows={4}
              className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm resize-y"
              data-testid="legal-drawer-summary"
            />
          </LabeledField>

          <div className="text-[11px] text-slate-500 font-mono-tab">
            Created {fmtDate(data.created_at)}{data.updated_at ? ` · Updated ${fmtDate(data.updated_at)}` : ""}
          </div>

          {/* Documents */}
          <div className="pt-2 border-t border-slate-100">
            <div className="flex items-center justify-between mb-3">
              <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 flex items-center gap-1.5">
                <FolderLock size={11} /> Documents ({documents.length})
              </div>
              <label className="pm-btn-secondary h-8 px-3 rounded-lg text-[11px] font-bold flex items-center gap-1.5 cursor-pointer" data-testid="legal-drawer-upload">
                {uploading ? <Loader2 size={11} className="animate-spin" /> : <Upload size={11} />}
                {uploading ? "Uploading…" : "Upload"}
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  onChange={(e) => uploadDoc(e.target.files?.[0])}
                  data-testid="legal-drawer-upload-input"
                />
              </label>
            </div>
            {documents.length === 0 ? (
              <div className="text-xs text-slate-500 italic">No documents yet. Upload PDFs, images, or contracts above.</div>
            ) : (
              <div className="space-y-1.5">
                {documents.map((d) => (
                  <div key={d.id} className="flex items-center gap-3 px-3 py-2 rounded-lg border border-slate-200" data-testid={`legal-drawer-doc-${d.id}`}>
                    <FileText size={14} className="text-emerald-600 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-bold text-slate-900 truncate" title={d.filename}>{d.filename}</div>
                      <div className="text-[11px] text-slate-500 font-mono-tab">
                        {(d.mime || "").split("/")[1]?.toUpperCase() || "FILE"} · {humanSize(d.size)} · {fmtDate(d.created_at)}
                        {d.uploaded_by_email && <> · {d.uploaded_by_email}</>}
                      </div>
                    </div>
                    <button
                      onClick={() => viewDoc(d)}
                      className="h-8 px-2.5 rounded-lg flex items-center gap-1 text-[11px] font-bold text-slate-700 bg-slate-50 hover:bg-slate-100 border border-slate-200"
                      data-testid={`legal-drawer-doc-view-${d.id}`}
                    >
                      <Download size={11} /> Open
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {err && (
            <div className="text-xs font-bold text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2" data-testid="legal-drawer-error">
              {err}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-100 flex items-center gap-3 sticky bottom-0 bg-white">
          <button
            onClick={remove}
            className="h-10 px-3 rounded-xl text-xs font-bold text-rose-700 hover:bg-rose-50 inline-flex items-center gap-1.5"
            data-testid="legal-drawer-delete"
          >
            <Trash2 size={12} /> Delete case
          </button>
          <div className="flex-1" />
          <button onClick={onClose} className="pm-btn-secondary h-10 px-4 rounded-xl text-sm font-bold" data-testid="legal-drawer-cancel">
            Close
          </button>
          <button
            onClick={save}
            disabled={busy || !isDirty}
            className="pm-btn-primary h-10 px-5 rounded-xl text-sm font-bold flex items-center gap-1.5 disabled:opacity-40"
            data-testid="legal-drawer-save"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            Save changes
          </button>
        </div>
      </div>
    </div>
  );
}

function TmsTab() {
  const [filings, setFilings] = useState([]);
  useEffect(() => {
    api.get("/legal/tms-filings").then((r) => setFilings(r.data || [])).catch(() => setFilings([]));
  }, []);
  return (
    <div className="space-y-4" data-testid="tms-list">
      <div className="pm-card p-5">
        <h3 className="font-display text-xl font-bold tracking-tight mb-1">FIFA TMS filing workflow</h3>
        <p className="text-sm text-slate-500 mb-3">Auto-pulls data from the client record. Flags missing/mismatched fields before submission.</p>
        <ol className="text-xs text-slate-700 space-y-1 list-decimal list-inside">
          <li>Player details (passport, DOB) — auto from client record</li>
          <li>Club details (from/to) — from active contract</li>
          <li>Contract dates &amp; fee — confirmed by agent</li>
          <li>Agent commission declaration — auto from representation agreement</li>
          <li>Submit before window close · auto-countdown enabled</li>
        </ol>
      </div>
      {filings.map((f) => {
        const closeDate = new Date(f.window_close);
        const daysLeft = Math.ceil((closeDate - new Date()) / (1000 * 60 * 60 * 24));
        const blocked = (f.missing_fields || []).length > 0;
        return (
          <div key={f.id} className="pm-card p-5" data-testid={`tms-${f.id}`}>
            <div className="flex items-center justify-between mb-2">
              <span className={`pm-pill ${blocked ? "pm-pill-red" : "pm-pill-green"}`}>
                {blocked ? "Missing fields" : "Ready"}
              </span>
              <span className="pm-pill pm-pill-slate">{f.status}</span>
              <span className="font-mono-tab text-xs text-slate-500">Window closes in {daysLeft}d · {fmtDate(f.window_close)}</span>
            </div>
            <div className="font-display font-bold text-lg">{f.from_club} → {f.to_club}</div>
            <div className="text-xs text-slate-500">Transfer fee {fmtMoney(f.transfer_fee_eur)} · Agent commission {fmtMoney(f.agent_commission_eur)}</div>
            {blocked && (
              <div className="mt-3 text-xs text-red-700 bg-red-50 border border-red-200 px-3 py-2 rounded-lg">
                <b>Missing:</b> {f.missing_fields.join(", ")}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function BodiesTab() {
  const [bodies, setBodies] = useState([]);
  useEffect(() => { api.get("/legal/governing-bodies").then((r) => setBodies(r.data || [])); }, []);
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="governing-bodies-list">
      {bodies.map((b) => (
        <div key={b.id} className="pm-card p-5" data-testid={`body-${b.id}`}>
          <div className="flex items-center gap-2 mb-2">
            <Globe size={14} className="text-emerald-600" />
            <span className="pm-pill pm-pill-slate">{b.scope}</span>
          </div>
          <div className="font-display font-bold text-base leading-tight">{b.name}</div>
          <div className="text-xs text-slate-500 font-mono-tab mt-1">{b.contact}</div>
          <div className="mt-3 flex flex-wrap gap-1">
            {(b.tags || []).map((t) => <span key={t} className="pm-pill pm-pill-blue">{t}</span>)}
          </div>
          <button className="mt-3 text-xs font-bold text-emerald-700 hover:text-emerald-800" data-testid={`body-open-${b.id}`}>
            Open correspondence thread →
          </button>
        </div>
      ))}
    </div>
  );
}

function LicenceTab() {
  const [licence, setLicence] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [uploadErr, setUploadErr] = useState("");
  const [courses, setCourses] = useState([]);
  const [courseFormOpen, setCourseFormOpen] = useState(false);
  const [visionResult, setVisionResult] = useState(null);
  const [rescanning, setRescanning] = useState(false);

  const reload = async () => {
    const [l, c] = await Promise.all([
      api.get("/legal/licence"),
      api.get("/legal/courses"),
    ]);
    setLicence(l.data);
    setForm({
      licence_number: l.data.licence_number || "",
      issuing_body: l.data.issuing_body || "",
      issued_date: l.data.issued_date || "",
      expiry_date: l.data.expiry_date || "",
    });
    setCourses(c.data || []);
  };
  useEffect(() => { reload(); }, []);

  if (!licence) return <div className="text-sm text-slate-500">Loading…</div>;

  const tone =
    licence.alert_tier === "expired" ? "border-red-400 bg-red-50/70" :
    licence.alert_tier === "7d" ? "border-red-400 bg-red-50/70" :
    licence.alert_tier === "30d" ? "border-red-300 bg-red-50/60" :
    licence.alert_tier === "60d" ? "border-amber-300 bg-amber-50/60" :
    "border-emerald-300 bg-emerald-50/60";

  const dayColour =
    licence.alert_tier === "expired" || licence.alert_tier === "7d" || licence.alert_tier === "30d" ? "text-red-600" :
    licence.alert_tier === "60d" ? "text-amber-600" :
    "text-emerald-700";

  const alertMessage =
    licence.alert_tier === "expired" ? "LICENCE EXPIRED. You are not permitted to act as a FIFA intermediary until you renew."
    : licence.alert_tier === "7d" ? "URGENT — Less than 7 days remaining. Renew immediately."
    : licence.alert_tier === "30d" ? "Renewal due within 30 days. Submit paperwork now."
    : licence.alert_tier === "60d" ? "Renewal window open. Start collating your CPD certificates."
    : null;

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/legal/licence", form);
      setLicence(r.data);
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  const uploadDoc = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploadErr("");
    setVisionResult(null);
    const fd = new FormData();
    fd.append("file", f);
    try {
      const r = await api.post("/legal/licence/document", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setLicence(r.data);
      // Surface Claude Vision extraction outcome so the agent immediately
      // sees what was auto-filled onto their credit-card widget.
      if (r.data?.vision_extracted) {
        setVisionResult(r.data.vision_extracted);
      }
    } catch (err) {
      setUploadErr(err?.response?.data?.detail || "Upload failed");
    } finally {
      e.target.value = "";
    }
  };

  const rescanDoc = async () => {
    setUploadErr("");
    setVisionResult(null);
    setRescanning(true);
    try {
      const r = await api.post("/legal/licence/rescan");
      setLicence(r.data);
      if (r.data?.vision_extracted) setVisionResult(r.data.vision_extracted);
    } catch (err) {
      setUploadErr(err?.response?.data?.detail || "Re-scan failed");
    } finally {
      setRescanning(false);
    }
  };

  const deleteDoc = async () => {
    if (!confirm("Remove the uploaded licence PDF?")) return;
    const r = await api.delete("/legal/licence/document");
    setLicence(r.data);
  };

  return (
    <div className="max-w-3xl space-y-5" data-testid="licence-tab">
      {/* Headline card */}
      <div className={`pm-card p-6 border ${tone}`} data-testid="licence-headline-card">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <ShieldCheck size={16} className="text-emerald-600" />
            <span className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">FIFA intermediary compliance</span>
          </div>
          <div className="flex items-center gap-2">
            <a
              href="https://agents.fifa.com"
              target="_blank"
              rel="noopener noreferrer"
              className="pm-btn-secondary h-8 px-3 rounded-lg text-xs font-bold flex items-center gap-1 bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100"
              data-testid="licence-fifa-platform-link"
              title="Open the FIFA Football Agent Platform in a new tab"
            >
              <ExternalLink size={11} /> FIFA Agent Platform
            </a>
            {!editing && (
              <button
                onClick={() => setEditing(true)}
                className="pm-btn-secondary h-8 px-3 rounded-lg text-xs font-bold flex items-center gap-1"
                data-testid="licence-edit-btn"
              >
                <Pencil size={11} /> Edit
              </button>
            )}
          </div>
        </div>

        {!editing ? (
          <>
            <div className="font-display font-black text-3xl tracking-tight" data-testid="licence-number">{licence.licence_number}</div>
            <div className="text-sm text-slate-600 mt-1">Issued by {licence.issuing_body}</div>
            <div className="mt-4 grid grid-cols-3 gap-3 text-sm">
              <Field label="Issued" value={fmtDate(licence.issued_date)} />
              <Field label="Expires" value={fmtDate(licence.expiry_date)} testid="licence-expiry-display" />
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Days left</div>
                <div className={`font-mono-tab font-black text-lg ${dayColour}`} data-testid="licence-days-left">
                  {licence.expired ? `${Math.abs(licence.days_to_expiry)} overdue` : licence.days_to_expiry}
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-1" data-testid="licence-edit-form">
            <Input label="Licence number" value={form.licence_number} onChange={(v) => setForm((s) => ({ ...s, licence_number: v }))} testid="licence-edit-number" />
            <Input label="Issuing FA" value={form.issuing_body} onChange={(v) => setForm((s) => ({ ...s, issuing_body: v }))} placeholder="e.g. KNVB, RFEF, FA" testid="licence-edit-body" />
            <Input label="Issued date" type="date" value={form.issued_date} onChange={(v) => setForm((s) => ({ ...s, issued_date: v }))} testid="licence-edit-issued" />
            <Input label="Expiry date" type="date" value={form.expiry_date} onChange={(v) => setForm((s) => ({ ...s, expiry_date: v }))} testid="licence-edit-expiry" />
            <div className="sm:col-span-2 flex items-center gap-2 pt-1">
              <button onClick={save} disabled={saving} className="pm-btn-primary h-9 px-4 rounded-lg text-xs font-bold flex items-center gap-1.5 disabled:opacity-50" data-testid="licence-save-btn">
                <Save size={11} /> {saving ? "Saving…" : "Save"}
              </button>
              <button onClick={() => { setEditing(false); reload(); }} className="text-slate-500 hover:text-slate-800 text-xs font-bold" data-testid="licence-cancel-btn">
                Cancel
              </button>
            </div>
          </div>
        )}

        {!editing && alertMessage && (
          <div className={`mt-4 text-xs px-3 py-2 rounded-lg flex items-center gap-2 ${
            licence.alert_tier === "60d"
              ? "text-amber-800 bg-amber-100/60 border border-amber-200"
              : "text-red-800 bg-red-100/60 border border-red-200"
          }`} data-testid="licence-alert-banner">
            <AlertTriangle size={12} /> {alertMessage}
          </div>
        )}
      </div>

      {/* Document upload card */}
      <div className="pm-card p-6" data-testid="licence-doc-card">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">Licence PDF</div>
            <div className="text-sm text-slate-700 mt-0.5">
              {licence.has_document
                ? <>Uploaded: <span className="font-bold">{licence.document_filename}</span> <span className="text-xs text-slate-400">({Math.round((licence.document_size || 0) / 1024)} KB)</span></>
                : "No file uploaded yet."}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {licence.has_document && (
              <a
                href={`${process.env.REACT_APP_BACKEND_URL}/api/legal/licence/document`}
                target="_blank"
                rel="noreferrer"
                onClick={(e) => {
                  // Send the bearer token via a fetch-then-blob approach.
                  e.preventDefault();
                  const token = localStorage.getItem("pm_token");
                  fetch(`${process.env.REACT_APP_BACKEND_URL}/api/legal/licence/document`, {
                    headers: { Authorization: `Bearer ${token}` },
                  })
                    .then((r) => r.blob())
                    .then((b) => window.open(URL.createObjectURL(b), "_blank"));
                }}
                className="pm-btn-secondary h-8 px-3 rounded-lg text-xs font-bold flex items-center gap-1"
                data-testid="licence-download-btn"
              >
                <Download size={11} /> View
              </a>
            )}
            {licence.has_document && (
              <button
                onClick={rescanDoc}
                disabled={rescanning}
                className="pm-btn-secondary h-8 px-3 rounded-lg text-xs font-bold flex items-center gap-1 bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100 disabled:opacity-50"
                data-testid="licence-rescan-btn"
                title="Re-run Claude Vision OCR to extract licence metadata"
              >
                <Sparkles size={11} className={rescanning ? "animate-pulse" : ""} /> {rescanning ? "Scanning…" : "Re-scan"}
              </button>
            )}
            <label className="pm-btn-primary h-8 px-3 rounded-lg text-xs font-bold flex items-center gap-1 cursor-pointer" data-testid="licence-upload-btn">
              <Upload size={11} /> {licence.has_document ? "Replace" : "Upload"}
              <input type="file" accept="application/pdf,image/*" onChange={uploadDoc} className="hidden" data-testid="licence-upload-input" />
            </label>
            {licence.has_document && (
              <button onClick={deleteDoc} className="h-8 w-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-red-600 hover:bg-red-50" data-testid="licence-delete-doc-btn">
                <Trash2 size={12} />
              </button>
            )}
          </div>
        </div>
        {uploadErr && <div className="text-xs text-red-600 mt-1">{uploadErr}</div>}
        {visionResult && <VisionExtractionBanner result={visionResult} onDismiss={() => setVisionResult(null)} />}
        <p className="text-xs text-slate-500 mt-2">Stored encrypted at rest. Only you and the workspace owner can download.</p>
      </div>

      {/* Continuing education */}
      <div className="pm-card p-6" data-testid="cpd-card">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <GraduationCap size={14} className="text-slate-500" />
            <span className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">
              Continuing education
              {courses.length > 0 && <span className="ml-2 text-slate-400 normal-case tracking-normal">· {courses.length} logged · {courses.reduce((s, c) => s + (c.cpd_hours || 0), 0)} CPD hours</span>}
            </span>
          </div>
          <button onClick={() => setCourseFormOpen(true)} className="pm-btn-secondary h-8 px-3 rounded-lg text-xs font-bold flex items-center gap-1" data-testid="add-course-btn">
            <Plus size={11} /> Log course
          </button>
        </div>

        {courseFormOpen && (
          <CourseForm onClose={() => setCourseFormOpen(false)} onSaved={() => { setCourseFormOpen(false); reload(); }} />
        )}

        {courses.length === 0 && !courseFormOpen ? (
          <div className="text-xs italic text-slate-400 py-6 text-center">No courses logged yet. FIFA requires ongoing CPD to keep your licence active.</div>
        ) : (
          <div className="space-y-2 mt-3">
            {courses.map((c) => (
              <CourseRow key={c.id} course={c} onChanged={reload} />
            ))}
          </div>
        )}
      </div>

      <div className="pm-card p-6">
        <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 mb-2">Per-deal intermediary declarations</div>
        <p className="text-sm text-slate-600">For every completed transfer, an intermediary declaration must be filed with the relevant FA within 14 days. Outstanding declarations surface in the urgent feed on the dashboard.</p>
      </div>
    </div>
  );
}

function Field({ label, value, testid }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">{label}</div>
      <div className="font-mono-tab font-bold" data-testid={testid}>{value || "—"}</div>
    </div>
  );
}

function Input({ label, value, onChange, type = "text", placeholder, testid }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">{label}</div>
      <input
        type={type}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full h-9 px-3 rounded-lg border border-slate-200 text-sm font-mono-tab"
        data-testid={testid}
      />
    </div>
  );
}

function CourseForm({ onClose, onSaved }) {
  const [course_name, setName] = useState("");
  const [provider, setProvider] = useState("");
  const [completed_date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [cpd_hours, setHours] = useState("");
  const [notes, setNotes] = useState("");
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const submit = async () => {
    if (!course_name) { setErr("Course name required"); return; }
    setSaving(true); setErr("");
    try {
      const fd = new FormData();
      fd.append("course_name", course_name);
      fd.append("completed_date", completed_date);
      if (provider) fd.append("provider", provider);
      if (cpd_hours) fd.append("cpd_hours", cpd_hours);
      if (notes) fd.append("notes", notes);
      if (file) fd.append("file", file);
      await api.post("/legal/courses", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onSaved();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-xl border border-slate-200 p-4 bg-slate-50/50 space-y-3" data-testid="course-form">
      <div className="flex items-center justify-between">
        <div className="text-xs font-bold text-slate-700">Log a CPD course</div>
        <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={12} /></button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Input label="Course name *" value={course_name} onChange={setName} placeholder="e.g. FIFA Ethics Refresher 2026" testid="course-name-input" />
        <Input label="Provider" value={provider} onChange={setProvider} placeholder="FIFA / FA / EFAA" testid="course-provider-input" />
        <Input label="Completed on *" type="date" value={completed_date} onChange={setDate} testid="course-date-input" />
        <Input label="CPD hours" type="number" value={cpd_hours} onChange={setHours} placeholder="e.g. 4" testid="course-hours-input" />
      </div>
      <textarea
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        placeholder="Notes (optional)"
        rows={2}
        className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm"
        data-testid="course-notes-input"
      />
      <div className="flex items-center gap-3">
        <label className="pm-btn-secondary h-8 px-3 rounded-lg text-xs font-bold flex items-center gap-1 cursor-pointer" data-testid="course-cert-label">
          <Upload size={11} /> {file ? "Replace certificate" : "Attach certificate"}
          <input type="file" accept="application/pdf,image/*" onChange={(e) => setFile(e.target.files?.[0] || null)} className="hidden" data-testid="course-cert-input" />
        </label>
        {file && <span className="text-xs text-slate-600">{file.name}</span>}
        <div className="ml-auto flex items-center gap-2">
          <button onClick={onClose} className="text-slate-500 hover:text-slate-800 text-xs font-bold">Cancel</button>
          <button onClick={submit} disabled={saving} className="pm-btn-primary h-9 px-4 rounded-lg text-xs font-bold flex items-center gap-1.5 disabled:opacity-50" data-testid="course-save-btn">
            <Save size={11} /> {saving ? "Saving…" : "Save course"}
          </button>
        </div>
      </div>
      {err && <div className="text-xs text-red-600">{err}</div>}
    </div>
  );
}

function CourseRow({ course, onChanged }) {
  const onDelete = async () => {
    if (!confirm(`Delete '${course.course_name}'?`)) return;
    await api.delete(`/legal/courses/${course.id}`);
    onChanged();
  };
  const onDownload = () => {
    const token = localStorage.getItem("pm_token");
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/legal/courses/${course.id}/certificate`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.blob())
      .then((b) => window.open(URL.createObjectURL(b), "_blank"));
  };
  return (
    <div className="rounded-lg border border-slate-200 px-3 py-2.5 flex items-center gap-3" data-testid={`course-row-${course.id}`}>
      <CheckCircle2 size={14} className="text-emerald-500 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-bold text-slate-800 truncate">{course.course_name}</div>
        <div className="text-[11px] text-slate-500">
          {course.provider || "—"} · {fmtDate(course.completed_date)}
          {course.cpd_hours ? ` · ${course.cpd_hours}h CPD` : ""}
          {course.has_certificate ? " · 📎 certificate" : ""}
        </div>
      </div>
      {course.has_certificate && (
        <button onClick={onDownload} className="h-8 w-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-100" title="View certificate" data-testid={`course-cert-download-${course.id}`}>
          <Download size={12} />
        </button>
      )}
      <button onClick={onDelete} className="h-8 w-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-red-600 hover:bg-red-50" data-testid={`course-delete-${course.id}`}>
        <Trash2 size={12} />
      </button>
    </div>
  );
}


function VisionExtractionBanner({ result, onDismiss }) {
  const fields = result?.fields || {};
  const labels = {
    licence_number: "Licence number",
    holder_name: "Holder name",
    issuing_body: "Issuing body",
    issued_date: "Issued date",
    expiry_date: "Expiry date",
  };
  const found = Object.entries(labels).filter(([k]) => fields[k]);
  const missing = Object.entries(labels).filter(([k]) => !fields[k]);
  if (!result?.used_vision) {
    return (
      <div className="mt-3 text-[11px] text-slate-500 bg-slate-50 border border-slate-200 rounded-lg p-2.5 flex items-center justify-between" data-testid="vision-banner-skipped">
        <span>Vision OCR unavailable — fields not auto-filled. Use the Edit button above to enter them manually.</span>
        <button onClick={onDismiss} className="text-slate-400 hover:text-slate-700"><X size={11} /></button>
      </div>
    );
  }
  if (found.length === 0) {
    return (
      <div className="mt-3 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2.5 flex items-center justify-between" data-testid="vision-banner-empty">
        <span>Claude scanned the file but couldn't read any licence fields. Please Edit and enter them by hand so the dashboard credit card stays accurate.</span>
        <button onClick={onDismiss} className="text-amber-600 hover:text-amber-900"><X size={11} /></button>
      </div>
    );
  }
  return (
    <div className="mt-3 text-xs bg-emerald-50 border border-emerald-200 rounded-lg p-3" data-testid="vision-banner-success">
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1">
          <div className="font-bold text-emerald-800 flex items-center gap-1.5 mb-1.5">
            <Sparkles size={11} /> Claude extracted {found.length} field{found.length === 1 ? "" : "s"} from your licence
            {result.confidence && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 uppercase font-bold tracking-wider">{result.confidence}</span>}
          </div>
          <div className="grid grid-cols-2 gap-x-4 gap-y-1 font-mono-tab text-[11px] text-slate-700" data-testid="vision-banner-fields">
            {found.map(([k, label]) => (
              <div key={k}><span className="text-slate-500">{label}:</span> <span className="font-bold">{fields[k]}</span></div>
            ))}
          </div>
          {missing.length > 0 && (
            <div className="mt-2 text-[10.5px] text-amber-700">
              Couldn't read: {missing.map(([, l]) => l).join(", ")}. Use Edit above to fill those in.
            </div>
          )}
        </div>
        <button onClick={onDismiss} className="text-emerald-600 hover:text-emerald-900 shrink-0"><X size={11} /></button>
      </div>
    </div>
  );
}
